import React, { useEffect, useState } from "react";
import {
  Paper,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import agent from "../../app/api/agent";
import DataTable from "../../app/common/DataTable";
import { useStore } from "../../app/stores/store";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";
import { Tenant } from "../../app/models/tenant";

const emptyFormData = {
  name: "",
  title: "",
  domain: "",
  minimumMembershipFee: 120,
  individualMembershipFee: 240,
  familyMembershipFee: 360,
  allowSepaPayments: false,
  street: "",
  city: "",
  zipCode: "",
  country: "",
  contactPhone: "",
  contactName: "",
  contactEmail: "",
  bankName: "",
  bankIBAN: "",
  bankBIC: "",
  donationBankName: "",
  donationIBAN: "",
  donationBIC: "",
};

export default function CreateTenantForm() {
  const [formData, setFormData] = useState<Tenant>(emptyFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const tenantStore = useStore((s) => s.tenantStore);
  const modalStore = useStore((s) => s.modalStore);
  const { loadTenants } = tenantStore;
  const columns = [
    { key: "name", header: "Ime džemata" },
    { key: "title", header: "Naslov (Header)" },
    { key: "city", header: "Grad" },
    { key: "contactEmail", header: "Kontakt email" },
  ];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox"
        ? checked
        : (name === "minimumMembershipFee" || name === "individualMembershipFee" || name === "familyMembershipFee"
          ? Number(value)
          : value)
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await agent.Tenants.update({ ...formData, id: editingId });
      } else {
        await agent.Tenants.create(formData);
      }
      loadTenants();
      resetFormData();
    } catch (error) {
      console.error("Error saving tenant:", error);
    }
  };

  const resetFormData = () => {
    setFormData(emptyFormData);
    setEditingId(null);
  };

  const handleRowClick = (tenant: Tenant) => {
    setEditingId(tenant.id || null);
    setFormData({
      name: tenant.name || "",
      title: tenant.title || "",
      domain: tenant.domain || "",
      minimumMembershipFee: tenant.minimumMembershipFee ?? 120,
      individualMembershipFee: tenant.individualMembershipFee ?? 240,
      familyMembershipFee: tenant.familyMembershipFee ?? 360,
      allowSepaPayments: tenant.allowSepaPayments ?? false,
      street: tenant.street || "",
      city: tenant.city || "",
      zipCode: tenant.zipCode || "",
      country: tenant.country || "",
      contactPhone: tenant.contactPhone || "",
      contactName: tenant.contactName || "",
      contactEmail: tenant.contactEmail || "",
      bankName: tenant.bankName || "",
      bankIBAN: tenant.bankIBAN || "",
      bankBIC: tenant.bankBIC || "",
      donationBankName: tenant.donationBankName || "",
      donationIBAN: tenant.donationIBAN || "",
      donationBIC: tenant.donationBIC || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    await tenantStore.deleteTenant(id);
    modalStore.closeModal();
    if (editingId === id) resetFormData();
  };

  const actionButtons = [
    {
      icon: <DeleteIcon color="error" />,
      onClick: (event: React.MouseEvent, item: Tenant) => {
        event.stopPropagation();
        modalStore.openModal(
          <ConfirmComponent
            onYes={async () => handleDelete(item.id!)}
            onCancel={modalStore.closeModal}
          />
        );
      },
    },
  ];

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h5" gutterBottom>
            {editingId ? "Uredi Džemat" : "Dodaj Džemat"}
          </Typography>
          {editingId && (
            <Button variant="outlined" onClick={resetFormData}>
              Otkaži uređivanje
            </Button>
          )}
        </Box>
        {editingId && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Registracijski link</Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {`${window.location.origin}/register-member?tenant=${editingId}`}
            </Typography>
          </Box>
        )}
        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Ime džemata"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Naslov (prikazan u headeru)"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                fullWidth
                helperText="Npr. 'IGBD Köln' - prikazuje se u headeru aplikacije"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Domena"
                name="domain"
                value={formData.domain}
                onChange={handleInputChange}
                fullWidth
                helperText="Npr. dzemat-koeln.de"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Minimalna članarina"
                name="minimumMembershipFee"
                type="number"
                value={formData.minimumMembershipFee}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Pojedinačna članarina"
                name="individualMembershipFee"
                type="number"
                value={formData.individualMembershipFee}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Porodična članarina"
                name="familyMembershipFee"
                type="number"
                value={formData.familyMembershipFee}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.allowSepaPayments} onChange={handleInputChange} name="allowSepaPayments" />}
                label="Omogući SEPA (Konto) plaćanja"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="Ulica"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Poštanski broj"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Grad"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Država"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }} >
              <TextField
                label="Kontakt ime"
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }} >
              <TextField
                label="Kontakt telefon"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }} >
              <TextField
                label="Kontakt e-mail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
                Bankverbindung (Mitgliedsbeitrag)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Bankname"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="IBAN"
                name="bankIBAN"
                value={formData.bankIBAN}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="BIC"
                name="bankBIC"
                value={formData.bankBIC}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
                Allgemeines Spendenkonto
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Bankname"
                name="donationBankName"
                value={formData.donationBankName}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="IBAN"
                name="donationIBAN"
                value={formData.donationIBAN}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="BIC"
                name="donationBIC"
                value={formData.donationBIC}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ textAlign: "right" }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
                {editingId ? "Spremi izmjene" : "Kreiraj džemat"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <DataTable
          columns={columns}
          data={tenantStore.tableData}
          onClick={handleRowClick}
          actionButtons={actionButtons}
        />
      </Paper>
    </>
  );
}
